import { useState } from 'react';
import type { FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ActivateResponse } from '@nadoutong/contracts';
import { activate as apiActivate } from '../api/auth.ts';
import { formatCnyCents, formatTimestamp } from '../components/formatters.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, buttonClass, cx } from '../ui/index.ts';
import { AUTH_LINK_CLASS, AuthPageFrame } from './auth/AuthPageFrame.tsx';
import { isAlreadyRegistered, localizeAuthError } from './auth/authErrors.ts';
import type { AuthErrorKeys } from './auth/authErrors.ts';

/**
 * Invite-code activation — the new-customer entry point (epic #1475).
 *
 * `POST /v1/auth/activate` does everything in one round-trip: account,
 * org, customer-manager binding, trial credit, and the session cookie. So
 * a success here means the visitor is already signed in; all this page
 * does afterwards is hydrate the session context and show them what they
 * got.
 *
 * Two deliberate departures from the sibling auth pages:
 *
 *   - No `status === 'authenticated'` redirect guard. Login and Register
 *     bounce an already-signed-in visitor to /dashboard, but activation
 *     ITSELF authenticates — that guard would fire on our own success and
 *     swallow the grant card before anyone read it. The tradeoff is that
 *     a signed-in visitor can open this page; submitting just returns
 *     EMAIL_ALREADY_REGISTERED, which is a correct answer.
 *
 *   - The grant is rendered from the response, never recomputed from the
 *     settings the ops console shows. Amount and expiry were snapshotted
 *     at issuance; a later settings change must not restate what this
 *     customer was actually given.
 */

const ACTIVATE_ERROR_KEYS: AuthErrorKeys = {
  INVITE_CODE_INVALID: 'activate.error.code_invalid',
  INVITE_CODE_QUOTA_EXHAUSTED: 'activate.error.quota_exhausted',
  EMAIL_ALREADY_REGISTERED: 'activate.error.email_taken',
  RATE_LIMITED: 'activate.error.rate_limited',
};

type ActivateState =
  | { readonly kind: 'form' }
  | { readonly kind: 'done'; readonly grant: ActivateResponse['grant'] };

export function Activate(): JSX.Element {
  const { fetchImpl, refresh } = useSession();
  const t = useT();
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSignInHint, setShowSignInHint] = useState(false);
  const [state, setState] = useState<ActivateState>({ kind: 'form' });

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setShowSignInHint(false);
    const trimmedEmail = email.trim();
    const trimmedCode = inviteCode.trim();
    if (trimmedEmail.length === 0 || trimmedCode.length === 0) {
      setError(t('activate.error.empty_fields'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiActivate(
        { email: trimmedEmail, inviteCode: trimmedCode },
        fetchImpl,
      );
      // The cookie is already set; this only populates the context so the
      // shell has a principal when the visitor follows the CTA.
      await refresh();
      setState({ kind: 'done', grant: response.grant });
    } catch (err) {
      setError(localizeAuthError(err, t, ACTIVATE_ERROR_KEYS));
      setShowSignInHint(isAlreadyRegistered(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (state.kind === 'done') {
    return (
      <AuthPageFrame
        title={t('activate.success.title')}
        description={t('activate.success.description')}
        width="lg"
      >
        <div
          className="flex flex-col gap-3 rounded-lg border border-ok-line bg-raised p-5"
          data-testid="activate-success"
          role="status"
        >
          <h2 className="m-0 flex items-center gap-2 text-section text-paper">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0 text-ok" />
            {t('activate.success.grant_title')}
          </h2>
          <p className="m-0 text-body text-paper-mid" data-testid="activate-grant">
            {t('activate.success.grant_body', {
              amount: formatCnyCents(state.grant.amountCnyCents),
              expiresAt: formatTimestamp(state.grant.expiresAt),
            })}
          </p>
          <p className="m-0 text-label text-paper-low">{t('activate.success.passwordless_hint')}</p>
          <Link
            to="/dashboard"
            data-testid="activate-success-cta"
            className={cx(buttonClass('primary'), 'mt-1 w-fit')}
          >
            {t('activate.success.cta')}
          </Link>
        </div>
      </AuthPageFrame>
    );
  }

  return (
    <AuthPageFrame
      title={t('activate.subtitle')}
      description={t('activate.description')}
      footer={
        <>
          <Link to="/login" data-testid="activate-login-link" className={AUTH_LINK_CLASS}>
            {t('activate.have_account')}
          </Link>
          <Link to="/register" data-testid="activate-register-link" className={AUTH_LINK_CLASS}>
            {t('activate.no_code')}
          </Link>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        <FormField label={t('activate.email')} fieldId="activate-email">
          <Input
            id="activate-email"
            type="email"
            mono
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={submitting}
            required
            data-testid="activate-email"
          />
        </FormField>
        <FormField label={t('activate.invite_code')} fieldId="activate-code">
          <Input
            id="activate-code"
            mono
            autoComplete="off"
            spellCheck={false}
            // Codes are minted uppercase; fold as the visitor types so the
            // field matches the card/message they are copying from. The
            // server accepts either case regardless.
            value={inviteCode}
            onChange={(e) => setInviteCode(e.currentTarget.value.toUpperCase())}
            disabled={submitting}
            required
            data-testid="activate-code"
          />
        </FormField>
        {error !== null ? (
          <div className="flex flex-col gap-1">
            <p className="m-0 text-label text-bad" role="alert" data-testid="activate-error">
              {error}
            </p>
            {showSignInHint ? (
              <Link to="/login" className={AUTH_LINK_CLASS} data-testid="activate-error-login-link">
                {t('activate.error.email_taken_cta')}
              </Link>
            ) : null}
          </div>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          data-testid="activate-submit"
          className="mt-1 h-9 w-full"
        >
          {submitting ? t('activate.submitting') : t('activate.submit')}
        </Button>
      </form>
    </AuthPageFrame>
  );
}
